import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import { AuthService, AuthState } from '../lib/services/AuthService';

interface AuthContextType extends AuthState {
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<{ user: any; session: any } | null>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (updates: { email?: string; data?: any }) => Promise<void>;
  deleteAccount: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
  });

  // Initialiser l'état d'authentification (timeout court pour ne pas bloquer l'affichage si l'API ne répond pas)
  useEffect(() => {
    const AUTH_INIT_TIMEOUT_MS = 4_000;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const initializeAuth = async () => {
      timeoutId = setTimeout(() => {
        setAuthState(prev => {
          if (prev.loading) {
            console.warn('Auth: timeout, affichage de la page de connexion.');
            return { ...prev, loading: false };
          }
          return prev;
        });
      }, AUTH_INIT_TIMEOUT_MS);

      try {
        const { data: sessionData, error: sessionError } = await AuthService.getSession();
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (sessionError) console.error('Session:', sessionError);
        if (sessionData?.session) {
          setAuthState({ user: sessionData.session.user, session: sessionData.session, loading: false, error: null });
        } else {
          setAuthState({ user: null, session: null, loading: false, error: null });
        }
      } catch (error) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        console.error('Erreur init auth:', error);
        setAuthState({ user: null, session: null, loading: false, error: null });
      }
    };

    initializeAuth();

    const { data: { subscription } } = AuthService.onAuthStateChange(
      async (event, session) => {
        console.log('Changement d\'état d\'authentification:', event, session);
        
        if (session) {
          setAuthState({
            user: session.user,
            session,
            loading: false,
            error: null,
          });
        } else {
          setAuthState({
            user: null,
            session: null,
            loading: false,
            error: null,
          });
        }
      }
    );

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Fonctions d'authentification
  const signInWithEmail = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await AuthService.signInWithEmail({ email, password });
      
      // Si une vérification est nécessaire, retourner cette information
      if (result && 'requiresVerification' in result && result.requiresVerification) {
        setAuthState(prev => ({ ...prev, loading: false }));
        return { requiresVerification: true };
      }
      
      if (result.error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: result.error?.message || 'Erreur lors de la connexion',
        }));
        throw result.error;
      }

      if (result.data?.user) {
        setAuthState({
          user: result.data.user,
          session: result.data.session,
          loading: false,
          error: null,
        });
      }
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Erreur lors de la connexion',
      }));
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data, error } = await AuthService.signUpWithEmail({ email, password });
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de l\'inscription',
        }));
        throw error;
      }

      if (data?.user) {
        setAuthState({
          user: data.user,
          session: data.session,
          loading: false,
          error: null,
        });
        return { user: data.user, session: data.session };
      }
      
      return null;
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de l\'inscription',
      }));
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { error } = await AuthService.signInWithGoogle();
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de la connexion Google',
        }));
        throw error;
      }

      // Pour OAuth, la redirection se fait automatiquement
      // L'état sera mis à jour via l'écouteur d'état
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la connexion Google',
      }));
      throw error;
    }
  };

  const signOut = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { error } = await AuthService.signOut();
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de la déconnexion',
        }));
        throw error;
      }

      setAuthState({
        user: null,
        session: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la déconnexion',
      }));
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
             const { error } = await AuthService.resetPassword(email);
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de la réinitialisation du mot de passe',
        }));
        throw error;
      }

      setAuthState(prev => ({ ...prev, loading: false }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la réinitialisation du mot de passe',
      }));
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
             const { error } = await AuthService.updatePassword(newPassword);
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de la mise à jour du mot de passe',
        }));
        throw error;
      }

      setAuthState(prev => ({ ...prev, loading: false }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la mise à jour du mot de passe',
      }));
      throw error;
    }
  };

  const updateProfile = async (updates: { email?: string; data?: any }) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { data, error } = await AuthService.updateProfile(updates);
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de la mise à jour du profil',
        }));
        throw error;
      }

      if (data?.user) {
        setAuthState(prev => ({
          ...prev,
          user: data.user,
          loading: false,
        }));
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la mise à jour du profil',
      }));
      throw error;
    }
  };

  const deleteAccount = async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { error } = await AuthService.deleteAccount();
      
      if (error) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Erreur lors de la suppression du compte',
        }));
        throw error;
      }

      setAuthState({
        user: null,
        session: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la suppression du compte',
      }));
      throw error;
    }
  };

  const resendVerificationEmail = async (email: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const { error } = await AuthService.resendVerificationEmail(email);
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message || "Erreur lors de l'envoi de l'email de vérification" }));
        throw error;
      }
      setAuthState(prev => ({ ...prev, loading: false }));
    } catch (error) {
      setAuthState(prev => ({ ...prev, loading: false }));
      throw error;
    }
  };

  const value: AuthContextType = {
    ...authState,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    deleteAccount,
    resendVerificationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
